import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Lang } from '@portal-contract';
import { detectLang, translate, type MessageKey, type Vars } from './translate';
import { readStoredLang, writeStoredLang } from './langStorage';

interface I18nValue {
  lang: Lang;
  /** True once the visitor used the toggle during this page load. */
  explicit: boolean;
  /** The visitor chose a language (toggle): remembered in this browser. */
  setLang: (lang: Lang) => void;
  /** The server told us the language (per-customer default or saved preference): not a visitor choice. */
  adoptLang: (lang: Lang) => void;
  t: (key: MessageKey, vars?: Vars) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ initialLang, children }: { initialLang?: Lang; children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(
    () => initialLang ?? readStoredLang() ?? detectLang(typeof navigator === 'undefined' ? undefined : navigator.languages),
  );
  const [explicit, setExplicit] = useState(false);

  // The `lang` attribute follows the toggle (plan 5.3, accessibility).
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    setExplicit(true);
    writeStoredLang(next);
  }, []);
  const adoptLang = useCallback((next: Lang) => setLangState(next), []);

  const value = useMemo<I18nValue>(
    () => ({ lang, explicit, setLang, adoptLang, t: (key, vars) => translate(lang, key, vars) }),
    [lang, explicit, setLang, adoptLang],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const v = useContext(I18nContext);
  if (!v) throw new Error('useI18n must be used inside <I18nProvider>');
  return v;
}
